import {
    useLayoutEffect,
    useEffect,
    useState,
    useRef,
    useCallback
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    FlatList,
    Image,
    View
} from "react-native";
import type {
    ManageCompanyObjectType,
    SelectedMyCompany,
    SelectedRequest,
    WaitingCompanyRequestTypes
} from "./type";
import stylesheet from "./stylesheet";
import {
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    BottomSheet,
    RowCard,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    useFocusEffect,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    PositionsObjectType
} from "../../../../../services/restAPI/actions/getPositions/types";
import type AppStackParamList from "../../../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    Trash2Icon,
    PencilOff,
    PenIcon,
    Check,
    Plus
} from "lucide-react-native";
import {
    RESTService
} from "../../../../../services/restAPI";
import logo from "../../../../../assets/images/gmkonyalogo.png";

const MyCompanies = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
    const route = useRoute<RouteProp<AppStackParamList, "MyCompanies">>();

    const {
        companies
    } = route.params;

    const {
        spaces,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [data,
        setData] = useState<Array<ManageCompanyObjectType>>([]);
    const [waitingRequsts,
        setWaitingRequests] = useState<Array<WaitingCompanyRequestTypes>>([]);
    const [status,
        setStatus] = useState<"active" | "waiting">("active");
    const [selectedCompany,
        setSelectedCompany] = useState<SelectedMyCompany>({
            companyID: "",
            isOwner: undefined
        });
    const [selectedRequest,
        setSelectedRequest] = useState<SelectedRequest>({
            requestID: "",
            requestType: ""
        });
    const [deleteDialog,
        setDeleteDialog] = useState({
        isVisible: false,
        companyID: ""
    });
    const [positionData,
        setPositionData] = useState<Array<PositionsObjectType>>([]);
    const [canceRequestVerificationDialog,
        setCanceRequestVerificationDialog] = useState(false);
    const [cancelRequestSuccesDialog,
        setCancelRequestSuccessDialog] = useState(false);
    const [cancelRequestID,
        setCancelRequestID] = useState("");
    const [loading,
        setLoading] = useState(true);

    const inset = useSafeAreaInsets();

    const editRef = useRef<IBottomSheetRef>(null);
    const editRequestRef = useRef<IBottomSheetRef>(null);

    useFocusEffect(
        useCallback(() => {
            getCompanies();
            getUpdateRequests();
        }, [])
    );

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={{
                    ...stylesheet.headerContainer,
                    paddingTop: inset.top,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.subtle
                }}
            >
                <TouchableOpacity
                    style={{
                        ...stylesheet.headerGoBack,
                        padding: spaces.spacingSm
                    }}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={{
                        ...stylesheet.headerTitle,
                        top: inset.top + spaces.spacingSm
                    }}
                >
                    <Text
                        variant="titleLargeSize"
                        style={stylesheet.headerTitleText}
                    >
                        {localize("editCompany")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    useEffect(() => {
        getPositions();
    }, []);

    const logoUri = Image.resolveAssetSource(logo)?.uri;

    const getCompanies = () => {
        RESTService.action("GetMyCompanies", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const _data: Array<ManageCompanyObjectType> = companies.map(e => {
                        const isOwner = res.payload.find(x => x.companyID === e.companyID)?.isMyCompany;
                        return {
                            address: e.address,
                            companyID: e.companyID,
                            companyLogo: e.companyLogo,
                            companyTitle: e.companyTitle,
                            isOwner: isOwner,
                            position: e.position,
                            positionID: e.positionID,
                            sectors: e.sectors,
                            website: e.website
                        };
                    });

                    setData(_data);
                }
                setLoading(false);
            }).catch((err) => {
                setLoading(false);
                console.log(err);
            });
    };

    const getUpdateRequests = () => {
        RESTService.action("GetCompanyUpdateRequests", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    console.log(res.payload);
                    const _data: Array<WaitingCompanyRequestTypes> = res.payload.map(e => {
                        const companyName = companies.find(x => x.companyID === e.companyID)?.companyTitle;
                        const userPosition = companies.find(x => x.companyID === e.companyID)?.position;
                        const companyPhotoURL = companies.find(x => x.companyID === e.companyID)?.companyLogo;

                        return {
                            _id: e._id,
                            companyName: companyName && companyName.length ? companyName: "",
                            createdAt: e.createdAt,
                            requestType: e.requestType,
                            updatedAt: e.updatedAt,
                            userPosition:  userPosition && userPosition.length ? userPosition: "",
                            aboutUs: e.aboutUs,
                            address: e.address,
                            companyID: e.companyID,
                            foundationYear: e.foundationYear,
                            companyPhotoURL: e.logoURL && e.logoURL.length ? e.logoURL: companyPhotoURL && companyPhotoURL.length ? companyPhotoURL : "",
                            isExports: e.isExports,
                            mail: e.mail,
                            phoneNumber: e.phoneNumber,
                            positionID: e.positionID,
                            sectorIDs: e.sectorIDs,
                            taxNumber: e.taxNumber,
                            taxOffice: e.taxOffice,
                            title: e.title,
                            website: e.website
                        };
                    });
                    setWaitingRequests(_data);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const getPositions = () => {
        RESTService.action("GetPositions", null)
            .then((res) => {

                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setPositionData(res.payload);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const cancelUpdateRequest = (id: string) => {
        RESTService.action("CancelRequestForUpdateCompany", {
            requestID: id
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const _waitingRequests: Array<WaitingCompanyRequestTypes> = JSON.parse(JSON.stringify(waitingRequsts));

                    const _index = _waitingRequests.findIndex(e => e._id === id);

                    if(_index !== -1) {
                        _waitingRequests.splice(_index, 1);
                        setWaitingRequests(_waitingRequests);
                    }

                    setCanceRequestVerificationDialog(false);
                    setCancelRequestSuccessDialog(true);
                    setCancelRequestID("");
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const removeCompany = (_companyID: string) => {
        RESTService.action("DeleteCompanyRequest", {
            companyID: _companyID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setDeleteDialog({
                        isVisible: false,
                        companyID: ""
                    });
                    NCoreUIKitToast.open({
                        title: localize("companyRemovalRequestCreated"),
                        type: "success",
                        isShowAction: false,
                        icon: ({
                            color
                        }) => <Check
                            color={colors.content.icon[color ? color : "default"]}
                        />
                    });
                    getUpdateRequests();
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderTabSwitch = () => {
        return <View
            style={{
                paddingHorizontal: spaces.spacingSm,
                paddingVertical: spaces.spacingSm,
                backgroundColor: colors.content.container.default,
                borderRadius: radiuses.md,
                ...stylesheet.tabBarSwitchContainer
            }}
        >
            <TouchableOpacity
                onPress={() => {
                    setStatus("active");
                }}
                style={{
                    ...stylesheet.tabBarButtonContainer,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingMd,
                    borderRadius: radiuses.md,
                    backgroundColor: status === "active" ? colors.content.container.subtle : colors.content.container.default,
                    marginRight: spaces.spacingMd
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                    style={{
                        marginRight: spaces.spacingSm
                    }}
                >
                    {localize("active")}
                </Text>
                <View
                    style={{
                        padding: spaces.spacingSm,
                        width: spaces.spacingXl,
                        height:spaces.spacingXl,
                        ...stylesheet.tabBarContentLength,
                        backgroundColor: colors.content.icon.success,
                        borderRadius: radiuses.full
                    }}
                >
                    <Text
                        variant="labelMediumSize"
                        color="onPrimary"
                    >
                        {data.length}
                    </Text>
                </View>
            </TouchableOpacity>
            <TouchableOpacity
                onPress={() => {
                    setStatus("waiting");
                }}
                style={{
                    ...stylesheet.tabBarButtonContainer,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingMd,
                    borderRadius: radiuses.md,
                    backgroundColor: status === "waiting" ? colors.content.container.subtle : colors.content.container.default,
                    marginRight: spaces.spacingMd
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                    style={{
                        marginRight: spaces.spacingSm
                    }}
                >
                    {localize("pending")}
                </Text>
                <View
                    style={{
                        padding: spaces.spacingSm,
                        width: spaces.spacingXl,
                        height:spaces.spacingXl,
                        ...stylesheet.tabBarContentLength,
                        backgroundColor: colors.content.container.primary,
                        borderRadius: radiuses.full
                    }}
                >
                    <Text
                        variant="labelMediumSize"
                        color="onPrimary"
                    >
                        {waitingRequsts.length}
                    </Text>
                </View>
            </TouchableOpacity>
        </View>;
    };

    const renderCompanies = () => {
        if(status === "active") {
            return <FlatList
                showsVerticalScrollIndicator={false}
                keyExtractor={(item) => item.companyID}
                renderItem={({
                    item
                }) => {
                    return <RowCard
                        isTransparentBackground={true}
                        icon={() => <View>
                            <Image
                                source={{
                                    uri: item.companyLogo || logoUri
                                }}
                                resizeMode="contain"
                                style={[
                                    stylesheet.companyLogo,
                                    {
                                        borderRadius: radiuses.md
                                    }
                                ]}
                            />
                        </View>}
                        titleVariant="titleMediumSize"
                        title={item.position}
                        subTitle={item.companyTitle}
                        key={item.companyID}
                        onPress={() => {
                            navigation.navigate("CompanyDetail", {
                                companyID: item.companyID
                            });
                        }}
                        rightIcon={() => <TouchableOpacity
                            onPress={() => {
                                editRef.current?.open();
                                setSelectedCompany({
                                    companyID: item.companyID,
                                    isOwner: item.isOwner
                                });
                            }}
                            style={{
                                padding: spaces.spacingSm
                            }}
                        >
                            <PenIcon/>
                        </TouchableOpacity>}
                        style={{
                            marginBottom: spaces.spacingSm
                        }}
                    />;
                }}
                horizontal={false}
                data={data}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />;
        }
        if(status === "waiting") {
            return <FlatList
                showsVerticalScrollIndicator={false}
                keyExtractor={(item) => item._id}
                renderItem={({
                    item
                }) => {
                    let postionTitle;

                    if(item.requestType === "new") {
                        postionTitle = positionData.find(x => x._id === item.positionID)?.value;
                    }

                    return <RowCard
                        isTransparentBackground={true}
                        icon={() => <View>
                            <Image
                                source={{
                                    uri: item.companyPhotoURL || logoUri
                                }}
                                resizeMode="contain"
                                style={[
                                    stylesheet.companyLogo,
                                    {
                                        borderRadius: radiuses.md
                                    }
                                ]}
                            />
                        </View>}
                        titleVariant="titleMediumSize"
                        title={item.requestType === "new" && postionTitle ? postionTitle : item.userPosition}
                        subTitle={item.requestType === "new" ? item.title : item.companyName}
                        key={item.companyID}
                        onPress={() => {
                            if(item.requestType === "new" || item.requestType === "edit") {
                                navigation.navigate("CompanyEditView", {
                                    updateRequest: {
                                        _id: item._id,
                                        createdAt: item.createdAt,
                                        requestType: item.requestType,
                                        updatedAt: item.updatedAt,
                                        aboutUs: item.aboutUs,
                                        address: item.address,
                                        companyID: item.companyID,
                                        foundationYear: item.foundationYear,
                                        isExports: item.isExports,
                                        mail: item.mail,
                                        phoneNumber: item.phoneNumber,
                                        positionID: item.positionID,
                                        sectorIDs: item.sectorIDs,
                                        taxNumber: item.taxNumber,
                                        logoURL: item.companyPhotoURL ? item.companyPhotoURL : item.logoURL,
                                        taxOffice: item.taxOffice,
                                        title: item.title,
                                        website: item.website
                                    }
                                });
                            }
                        }}
                        rightIcon={() => <TouchableOpacity
                            onPress={() => {
                                setSelectedRequest({
                                    requestID: item._id,
                                    requestType: item.requestType
                                });
                                editRequestRef.current?.open();
                            }}
                            style={{
                                padding: spaces.spacingSm
                            }}
                        >
                            <PenIcon/>
                        </TouchableOpacity>}
                        style={{
                            marginBottom: spaces.spacingMd
                        }}
                    />;
                }}
                horizontal={false}
                data={waitingRequsts}
            />;
        }
        else {
            return null;
        }
    };

    const renderCreateCompanyButton = () => {
        return <View
            style={stylesheet.createCompany}
        >
            <Button
                size="large"
                title={localize("addNewCompany")}
                variant="outline"
                customBorderColor="black"
                icon={() => <Plus/>}
                style={stylesheet.createCompanyButton}
                onPress={() => {
                    navigation.navigate("CreateCompany");
                }}
            />
        </View>;
    };

    const editSheet = () => {
        return <BottomSheet
            ref={editRef}
            isAutoHeight={true}
            onClose={() => {
                setSelectedCompany({
                    companyID: "",
                    isOwner: undefined
                });
            }}
            style={{
                borderTopLeftRadius: radiuses.xl,
                borderTopRightRadius: radiuses.xl
            }}
            renderHeader={() => <View
                style={{
                    padding: spaces.spacingXl,
                    ...stylesheet.editSheetHeader,
                    borderRadius: radiuses.sm,
                    backgroundColor: colors.content.container.subtle,
                    marginBottom: spaces.spacingMd
                }}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("editCompany")}
                </Text>
            </View>}
        >
            {
                selectedCompany.isOwner ? <TouchableOpacity
                    style={{
                        ...stylesheet.editSheetButton,
                        marginBottom: spaces.spacingMd
                    }}
                    onPress={() => {
                        navigation.navigate("CompanyEdit", {
                            companyID: selectedCompany.companyID
                        });
                        editRef.current?.close();
                    }}
                >
                    <View
                        style={{
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginRight: spaces.spacingMd,
                            backgroundColor: colors.content.container.subtle
                        }}
                    >
                        <PenIcon
                            color={colors.content.icon.warning}
                        />
                    </View>
                    <Text
                        variant="bodyLargeSize"
                        color="high"
                    >
                        {localize("edit")}
                    </Text>
                </TouchableOpacity> : null
            }
            <TouchableOpacity
                style={{
                    ...stylesheet.editSheetButton,
                    marginBottom: spaces.spacingMd
                }}
                onPress={() => {
                    setDeleteDialog({
                        isVisible: true,
                        companyID: selectedCompany.companyID
                    });
                    editRef.current?.close();
                }}
            >
                <View
                    style={{
                        padding: spaces.spacingMd,
                        borderRadius: radiuses.full,
                        marginRight: spaces.spacingMd,
                        backgroundColor: colors.content.container.subtle
                    }}
                >
                    <Trash2Icon
                        color={colors.content.icon.danger}
                    />
                </View>
                <Text
                    variant="bodyLargeSize"
                    color="high"
                >
                    {selectedCompany.isOwner ? localize("submitCompanyRemovalRequest") : localize("submitPositionTerminationRequest")}
                </Text>
            </TouchableOpacity>
        </BottomSheet>;
    };

    const editRequestSheet = () => {
        return <BottomSheet
            ref={editRequestRef}
            isAutoHeight={true}
            onClose={() => {
                setSelectedRequest({
                    requestID: "",
                    requestType: ""
                });
            }}
            style={{
                borderTopLeftRadius: radiuses.xl,
                borderTopRightRadius: radiuses.xl
            }}
            renderHeader={() => <View
                style={{
                    padding: spaces.spacingXl,
                    borderRadius: radiuses.sm,
                    ...stylesheet.editSheetHeader,
                    backgroundColor: colors.content.container.subtle,
                    marginBottom: spaces.spacingMd
                }}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("editPendingCompany")}
                </Text>
            </View>}
        >
            {
                selectedRequest.requestType === "edit" || selectedRequest.requestType === "new" ? <TouchableOpacity
                    style={{
                        ...stylesheet.editSheetButton,
                        marginBottom: spaces.spacingMd
                    }}
                    onPress={() => {
                        const _index = waitingRequsts.findIndex(e => e._id === selectedRequest.requestID);

                        if(_index !== -1) {
                            const _updateReq = waitingRequsts[_index];
                            navigation.navigate("CompanyEditView", {
                                updateRequest: {
                                    _id: _updateReq ? _updateReq._id : "",
                                    createdAt: _updateReq ? _updateReq.createdAt : "",
                                    requestType: _updateReq ? _updateReq.requestType : "",
                                    updatedAt: _updateReq ? _updateReq.updatedAt : "",
                                    aboutUs: _updateReq ? _updateReq.aboutUs :  "",
                                    address: _updateReq ? _updateReq.address : "",
                                    companyID: _updateReq ? _updateReq.companyID : "",
                                    foundationYear: _updateReq ? _updateReq.foundationYear : 2026,
                                    isExports: _updateReq ? _updateReq.isExports : false,
                                    mail: _updateReq ? _updateReq.mail : "",
                                    logoURL: _updateReq ? _updateReq.companyPhotoURL : "",
                                    phoneNumber: _updateReq ? _updateReq.phoneNumber : "",
                                    positionID: _updateReq ? _updateReq.positionID: "",
                                    sectorIDs: _updateReq ? _updateReq.sectorIDs : [],
                                    taxNumber: _updateReq ? _updateReq.taxNumber : "",
                                    taxOffice: _updateReq ? _updateReq.taxOffice: "",
                                    title: _updateReq ? _updateReq.title :  "",
                                    website: _updateReq ? _updateReq.website: ""
                                }
                            });
                        }
                        editRequestRef.current?.close();
                    }}
                >
                    <View
                        style={{
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginRight: spaces.spacingMd,
                            backgroundColor: colors.content.container.subtle
                        }}
                    >
                        <PenIcon
                            color={colors.content.icon.warning}
                        />
                    </View>
                    <Text
                        variant="bodyLargeSize"
                        color="high"
                    >
                        {localize("edit")}
                    </Text>
                </TouchableOpacity> : null
            }
            <TouchableOpacity
                style={{
                    ...stylesheet.editSheetButton,
                    marginBottom: spaces.spacingMd
                }}
                onPress={() => {
                    setCancelRequestID(selectedRequest.requestID);
                    setCanceRequestVerificationDialog(true);
                    editRequestRef.current?.close();
                }}
            >
                <View
                    style={{
                        padding: spaces.spacingMd,
                        borderRadius: radiuses.full,
                        marginRight: spaces.spacingMd,
                        backgroundColor: colors.content.container.subtle
                    }}
                >
                    <Trash2Icon
                        color={colors.content.icon.danger}
                    />
                </View>
                <Text
                    variant="bodyLargeSize"
                    color="high"
                >
                    {localize("withdrawRequest")}
                </Text>
            </TouchableOpacity>
        </BottomSheet>;
    };

    const renderDeleteDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.deleteDialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconContainer,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Trash2Icon
                        size={40}
                        color={colors.content.icon.danger}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.dialogTitle}
                >
                    {localize("confirmRemoveCompany")}
                </Text>
            </View>}
            content={localize("confirmSubmitCompanyRemovalRequest")}
            isVisible={deleteDialog.isVisible}
            onOverlayPress={() => {
                setDeleteDialog({
                    isVisible: false,
                    companyID: ""
                });
            }}
            primaryButtonProps={{
                onPress: () => {
                    removeCompany(deleteDialog.companyID);
                },
                type:"danger",
                title: localize("verify")
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setDeleteDialog({
                        isVisible: false,
                        companyID: ""
                    });
                }
            }}
            variant="yes-no"
        />;
    };

    const renderCancelVerificationDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.deleteDialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconContainer,
                        {
                            marginBottom: spaces.spacingLg,
                            padding: spaces.spacingLg,
                            backgroundColor: colors.content.container.warning,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <PencilOff
                        size={40}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.dialogTitle}
                >
                    {localize("confirmCancelEditRequest")}
                </Text>
            </View>}
            content={localize("cancelEditRequestDescription")}
            isVisible={canceRequestVerificationDialog}
            onOverlayPress={() => {
                setCanceRequestVerificationDialog(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    cancelUpdateRequest(cancelRequestID);
                },
                type: "warning",
                title: localize("cancel-it")
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setCanceRequestVerificationDialog(false);
                }
            }}
            variant="yes-no"
        />;
    };

    const renderCancelVerificationSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.deleteDialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconContainer,
                        {
                            backgroundColor: colors.content.container.warning,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("requestCancelled")}
                </Text>
            </View>}
            content={localize("editRequestWithdrawn")}
            isVisible={cancelRequestSuccesDialog}
            onOverlayPress={() => {
                setCancelRequestSuccessDialog(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    setCancelRequestSuccessDialog(false);
                },
                type: "warning",
                title: localize("goBack")
            }}
            variant="ok"
        />;
    };

    if(loading) {
        return <PageContainer>
            <ActivityIndicator
                size={"large"}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={true}
        backgroundColor="subtle"
    >
        {renderTabSwitch()}
        {renderCompanies()}
        {renderCreateCompanyButton()}
        {editSheet()}
        {renderDeleteDialog()}
        {editRequestSheet()}
        {renderCancelVerificationDialog()}
        {renderCancelVerificationSuccessDialog()}
    </PageContainer>;
};
export default MyCompanies;
